<script lang="ts" setup>
import { reactive, ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import type { Product } from '@/types'
import { uuid } from '@/composables'
import { formatCurrency } from '@/utils'
import { useAxios } from '@/composables/useAxios'
import { useAxiosError } from '@/composables/useAxiosError'
import { useNotifyStore, useProductsStore } from '@/stores'
import { STATUS_CODES } from '@/constants'

const { t } = useI18n()
const router = useRouter()

const props = defineProps<{
  products: Product[]
  title?: string
  subtitle?: string
  immutable?: boolean
  defaultCollapsed?: boolean
}>()

const emits = defineEmits<{
  (e: 'refresh'): void
}>()

const notifyStore = useNotifyStore()
const productsStore = useProductsStore()
const { handleAxiosError } = useAxiosError()

// Collapsible state - use prop for default
const collapsed = ref(props.defaultCollapsed ?? false)

// Filter state
const searchQuery = ref('')
const activeFilter = ref<'all' | 'active' | 'inactive'>('all')
const publicFilter = ref<'all' | 'public' | 'private'>('all')

// Filtered products
const filteredProducts = computed(() => {
  let result = props.products

  // Search filter
  if (searchQuery.value.trim()) {
    const query = searchQuery.value.toLowerCase().trim()
    result = result.filter((p) =>
      p.name.toLowerCase().includes(query) ||
      (p.description && p.description.toLowerCase().includes(query)) ||
      (p.slug && p.slug.toLowerCase().includes(query))
    )
  }

  // Active filter
  if (activeFilter.value !== 'all') {
    const isActive = activeFilter.value === 'active'
    result = result.filter((p) => p.active === isActive)
  }

  // Public filter
  if (publicFilter.value !== 'all') {
    const isPublic = publicFilter.value === 'public'
    result = result.filter((p) => p.public === isPublic)
  }

  return result
})

const hasActiveFilters = computed(() =>
  searchQuery.value.trim() !== '' ||
  activeFilter.value !== 'all' ||
  publicFilter.value !== 'all'
)

const clearFilters = () => {
  searchQuery.value = ''
  activeFilter.value = 'all'
  publicFilter.value = 'all'
}

const data = reactive({
  togglingProduct: null as string | null
})

const getDefaultPrice = (product: Product): string => {
  if (product.price) {
    const defaultPrice = product.price.filter((price) => (price.default ? price : null))
    if (
      defaultPrice &&
      defaultPrice.length > 0 &&
      defaultPrice[0]?.amount &&
      defaultPrice[0].amount !== 0
    ) {
      return formatCurrency(defaultPrice[0].amount / 100, 'EUR', 'de-DE')
    }
  }
  return 'N/A'
}

const handleNewProduct = () => {
  router.push({ name: 'dashboardAdminProductEdit', params: { id: 'newProd' } })
}

const goToProduct = (slug: string | undefined) => {
  if (!slug) return
  router.push({ name: 'dashboardAdminProductEdit', params: { id: slug } })
}

// Toggle active/public status
const toggleProductStatus = (product: Product, field: 'active' | 'public', event: Event) => {
  event.stopPropagation()
  if (data.togglingProduct || !product.slug) return

  data.togglingProduct = `${product.slug}-${field}`
  const newValue = !product[field]

  const updatedProduct = { ...product, [field]: newValue }

  // API uses POST /product with slug in body to determine update vs create
  useAxios<Product, Product>('POST', '/product', true, updatedProduct)
    .then((res) => {
      if (!res) return
      if (res.status === STATUS_CODES.OK) {
        const statusKey = field === 'active' ? 'activeEnabled' : 'publicEnabled'
        const messageKey = newValue ? statusKey : statusKey.replace('Enabled', 'Disabled')
        notifyStore.createNotify({
          id: uuid(),
          title: t('common.success'),
          message: t(`dashboard.admin.products.notifications.${messageKey}`, { name: product.name }),
          alertType: 'success'
        })
        productsStore.invalidateCache()
        emits('refresh')
      }
    })
    .catch((error) => {
      handleAxiosError(error)
    })
    .finally(() => {
      data.togglingProduct = null
    })
}

</script>

<template>
  <div class="product-table-container" :class="{ collapsed }">
    <!-- Section Header -->
    <div class="section-header" @click="collapsed = !collapsed">
      <div class="header-left">
        <button class="collapse-toggle" :class="{ collapsed }">
          <v-icon icon="mdi-chevron-down" size="20" />
        </button>
        <div class="table-icon" :class="{ 'icon-locked': immutable }">
          <v-icon :icon="immutable ? 'mdi-lock' : 'mdi-package-variant'" size="20" />
        </div>
        <div class="header-text">
          <h2 class="table-title">{{ title || $t('dashboard.admin.products.title') }}</h2>
          <p class="table-subtitle">{{ subtitle || $t('dashboard.admin.products.subtitle') }}</p>
        </div>
        <v-chip v-if="collapsed && products.length > 0" size="small" variant="tonal" class="count-badge">
          {{ products.length }}
        </v-chip>
      </div>
      <div class="header-actions" @click.stop>
        <button
          v-if="!immutable"
          class="create-btn"
          @click.prevent="handleNewProduct"
        >
          <v-icon icon="mdi-plus" size="18" />
          <span>{{ $t('dashboard.admin.products.newProduct') }}</span>
        </button>
      </div>
    </div>

    <!-- Collapsible Content -->
    <div v-show="!collapsed" class="collapsible-content">
      <!-- Filter Bar -->
      <div v-if="products.length > 0" class="filter-bar">
        <div class="search-wrapper">
          <v-icon icon="mdi-magnify" size="18" class="search-icon" />
          <input
            v-model="searchQuery"
            type="text"
            class="search-input"
            :placeholder="$t('dashboard.admin.products.search')"
          />
          <button
            v-if="searchQuery"
            type="button"
            class="clear-btn"
            @click="searchQuery = ''"
          >
            <v-icon icon="mdi-close" size="14" />
          </button>
        </div>
        <div class="filter-group">
          <span class="filter-label">{{ $t('dashboard.admin.products.table.active') }}:</span>
          <div class="filter-buttons">
            <button
              type="button"
              class="filter-btn"
              :class="{ active: activeFilter === 'all' }"
              @click="activeFilter = 'all'"
            >
              {{ $t('common.all') }}
            </button>
            <button
              type="button"
              class="filter-btn"
              :class="{ active: activeFilter === 'active' }"
              @click="activeFilter = 'active'"
            >
              <v-icon icon="mdi-check" size="14" />
            </button>
            <button
              type="button"
              class="filter-btn"
              :class="{ active: activeFilter === 'inactive' }"
              @click="activeFilter = 'inactive'"
            >
              <v-icon icon="mdi-close" size="14" />
            </button>
          </div>
        </div>
        <div class="filter-group">
          <span class="filter-label">{{ $t('dashboard.admin.products.table.public') }}:</span>
          <div class="filter-buttons">
            <button
              type="button"
              class="filter-btn"
              :class="{ active: publicFilter === 'all' }"
              @click="publicFilter = 'all'"
            >
              {{ $t('common.all') }}
            </button>
            <button
              type="button"
              class="filter-btn"
              :class="{ active: publicFilter === 'public' }"
              @click="publicFilter = 'public'"
            >
              <v-icon icon="mdi-check" size="14" />
            </button>
            <button
              type="button"
              class="filter-btn"
              :class="{ active: publicFilter === 'private' }"
              @click="publicFilter = 'private'"
            >
              <v-icon icon="mdi-close" size="14" />
            </button>
          </div>
        </div>
        <button
          v-if="hasActiveFilters"
          type="button"
          class="clear-filters-btn"
          @click="clearFilters"
        >
          <v-icon icon="mdi-filter-remove" size="16" />
          <span>Reset</span>
        </button>
      </div>

      <!-- Table -->
      <div class="glass-table">
        <div class="table-head">
          <div class="head-cell cell-name">{{ $t('dashboard.admin.products.table.name') }}</div>
          <div class="head-cell cell-price">{{ $t('dashboard.admin.products.table.price') }}</div>
          <div class="head-cell cell-status">{{ $t('dashboard.admin.products.table.active') }}</div>
          <div class="head-cell cell-status">{{ $t('dashboard.admin.products.table.public') }}</div>
        </div>

        <div class="table-body">
          <div v-if="products.length === 0" class="empty-state">
            <v-icon icon="mdi-package-variant-closed" size="48" />
            <span>{{ $t('dashboard.admin.products.table.empty') }}</span>
          </div>

          <div v-else-if="filteredProducts.length === 0" class="empty-state filter-empty">
            <v-icon icon="mdi-filter-remove-outline" size="36" />
            <span>{{ $t('dashboard.admin.products.noFilterResults') }}</span>
            <button type="button" class="reset-filter-btn" @click="clearFilters">
              {{ $t('dashboard.admin.products.resetFilters') }}
            </button>
          </div>

          <div
            v-for="product in filteredProducts"
            v-else
            :key="product.slug"
            class="table-row"
            @click.prevent="goToProduct(product.slug)"
          >
            <div class="row-cell cell-name">
              <div class="product-info">
                <span class="product-name">{{ product.name }}</span>
                <span v-if="product.description" class="product-desc">{{ product.description }}</span>
              </div>
            </div>
            <div class="row-cell cell-price">
              <span class="price-value">{{ getDefaultPrice(product) }}</span>
            </div>
            <div class="row-cell cell-status">
              <button
                v-if="!immutable"
                class="status-toggle"
                :class="product.active ? 'status-active' : 'status-inactive'"
                :disabled="data.togglingProduct === `${product.slug}-active`"
                :title="$t('dashboard.admin.products.table.toggleActive')"
                @click="toggleProductStatus(product, 'active', $event)"
              >
                <v-progress-circular
                  v-if="data.togglingProduct === `${product.slug}-active`"
                  indeterminate
                  size="14"
                  width="2"
                />
                <v-icon v-else :icon="product.active ? 'mdi-check' : 'mdi-close'" size="16" />
              </button>
              <div v-else class="status-badge" :class="product.active ? 'status-active' : 'status-inactive'">
                <v-icon :icon="product.active ? 'mdi-check' : 'mdi-close'" size="16" />
              </div>
            </div>
            <div class="row-cell cell-status">
              <button
                v-if="!immutable"
                class="status-toggle"
                :class="product.public ? 'status-active' : 'status-inactive'"
                :disabled="data.togglingProduct === `${product.slug}-public`"
                :title="$t('dashboard.admin.products.table.togglePublic')"
                @click="toggleProductStatus(product, 'public', $event)"
              >
                <v-progress-circular
                  v-if="data.togglingProduct === `${product.slug}-public`"
                  indeterminate
                  size="14"
                  width="2"
                />
                <v-icon v-else :icon="product.public ? 'mdi-check' : 'mdi-close'" size="16" />
              </button>
              <div v-else class="status-badge" :class="product.public ? 'status-active' : 'status-inactive'">
                <v-icon :icon="product.public ? 'mdi-check' : 'mdi-close'" size="16" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.product-table-container {
  background: rgba(15, 23, 48, 0.5);
  backdrop-filter: blur(12px);
  border: 1px solid rgba(120, 163, 255, 0.1);
  border-radius: 12px;
  overflow: hidden;
}

/* Section Header */
.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  border-bottom: 1px solid rgba(120, 163, 255, 0.08);
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover {
    background: rgba(120, 163, 255, 0.03);
  }

  .collapsed & {
    border-bottom: none;
  }
}

.collapse-toggle {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  background: transparent;
  border: none;
  border-radius: 6px;
  color: rgba(200, 218, 255, 0.5);
  cursor: pointer;
  transition: all 0.2s ease;
  margin-right: 4px;

  &:hover {
    background: rgba(120, 163, 255, 0.1);
    color: rgba(200, 218, 255, 0.8);
  }

  .v-icon {
    transition: transform 0.2s ease;
  }

  &.collapsed .v-icon {
    transform: rotate(-90deg);
  }
}

.count-badge {
  margin-left: 12px;
}

.collapsible-content {
  animation: slideDown 0.2s ease;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.header-left {
  display: flex;
  align-items: center;
  gap: 14px;
}

.header-actions {
  display: flex;
  gap: 10px;
}

.table-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: rgba(0, 176, 255, 0.1);
  border: 1px solid rgba(0, 176, 255, 0.2);
  border-radius: 10px;

  .v-icon {
    color: #00B0FF;
  }

  &.icon-locked {
    background: rgba(200, 218, 255, 0.05);
    border-color: rgba(200, 218, 255, 0.15);

    .v-icon {
      color: rgba(200, 218, 255, 0.5);
    }
  }
}

.header-text {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.table-title {
  margin: 0;
  font-size: 0.95rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.95);
}

.table-subtitle {
  margin: 0;
  font-size: 0.8rem;
  color: rgba(200, 218, 255, 0.5);
}

.create-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: rgba(76, 175, 80, 0.1);
  border: 1px solid rgba(76, 175, 80, 0.25);
  border-radius: 10px;
  color: #4CAF50;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(76, 175, 80, 0.15);
    border-color: rgba(76, 175, 80, 0.35);
  }
}

/* Filter Bar */
.filter-bar {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 12px 16px;
  background: rgba(15, 23, 48, 0.3);
  border-bottom: 1px solid rgba(120, 163, 255, 0.08);
  flex-wrap: wrap;
}

.search-wrapper {
  position: relative;
  flex: 1;
  min-width: 200px;
  max-width: 280px;
}

.search-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: rgba(200, 218, 255, 0.4);
  pointer-events: none;
}

.search-input {
  width: 100%;
  padding: 8px 36px;
  background: rgba(15, 23, 48, 0.5);
  border: 1px solid rgba(120, 163, 255, 0.15);
  border-radius: 8px;
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.85rem;
  outline: none;
  transition: all 0.2s ease;

  &::placeholder {
    color: rgba(200, 218, 255, 0.4);
  }

  &:focus {
    border-color: rgba(0, 176, 255, 0.4);
    background: rgba(15, 23, 48, 0.7);
  }
}

.clear-btn {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  padding: 0;
  background: rgba(200, 218, 255, 0.1);
  border: none;
  border-radius: 50%;
  color: rgba(200, 218, 255, 0.5);
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: rgba(200, 218, 255, 0.2);
    color: rgba(200, 218, 255, 0.8);
  }
}

.filter-group {
  display: flex;
  align-items: center;
  gap: 8px;
}

.filter-label {
  font-size: 0.8rem;
  color: rgba(200, 218, 255, 0.5);
}

.filter-buttons {
  display: flex;
  gap: 4px;
}

.filter-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 6px 10px;
  min-width: 32px;
  background: rgba(120, 163, 255, 0.08);
  border: 1px solid rgba(120, 163, 255, 0.15);
  border-radius: 6px;
  color: rgba(200, 218, 255, 0.6);
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: rgba(120, 163, 255, 0.12);
    color: rgba(200, 218, 255, 0.8);
  }

  &.active {
    background: rgba(0, 176, 255, 0.15);
    border-color: rgba(0, 176, 255, 0.4);
    color: #00b0ff;
  }
}

.clear-filters-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: rgba(244, 67, 54, 0.1);
  border: 1px solid rgba(244, 67, 54, 0.2);
  border-radius: 6px;
  color: #ef5350;
  font-size: 0.8rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: rgba(244, 67, 54, 0.15);
    border-color: rgba(244, 67, 54, 0.3);
  }
}

/* Table */
.glass-table {
  overflow: hidden;
}

.table-head {
  display: grid;
  grid-template-columns: 1fr 150px 100px 100px;
  padding: 14px 20px;
  background: rgba(255, 255, 255, 0.02);
  border-bottom: 1px solid rgba(120, 163, 255, 0.08);
}

.head-cell {
  font-size: 0.75rem;
  font-weight: 600;
  color: rgba(200, 218, 255, 0.5);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.cell-status {
  text-align: center;
}

/* Table Body - Scrollable */
.table-body {
  max-height: 400px;
  overflow-y: auto;

  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(120, 163, 255, 0.05);
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(120, 163, 255, 0.2);
    border-radius: 3px;

    &:hover {
      background: rgba(120, 163, 255, 0.3);
    }
  }
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 48px 20px;
  color: rgba(200, 218, 255, 0.4);

  .v-icon {
    opacity: 0.5;
  }

  span {
    font-size: 0.9rem;
  }

  &.filter-empty {
    padding: 32px 20px;
  }
}

.reset-filter-btn {
  margin-top: 8px;
  padding: 8px 16px;
  background: rgba(0, 176, 255, 0.12);
  border: 1px solid rgba(0, 176, 255, 0.25);
  border-radius: 8px;
  color: #00b0ff;
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(0, 176, 255, 0.2);
    border-color: rgba(0, 176, 255, 0.4);
  }
}

.table-row {
  display: grid;
  grid-template-columns: 1fr 150px 100px 100px;
  padding: 16px 20px;
  border-bottom: 1px solid rgba(120, 163, 255, 0.05);
  cursor: pointer;
  transition: all 0.2s ease;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: rgba(255, 255, 255, 0.03);
  }
}

.row-cell {
  display: flex;
  align-items: center;
}

.cell-status {
  justify-content: center;
}

.product-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.product-name {
  font-size: 0.95rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.9);
}

.product-desc {
  font-size: 0.8rem;
  color: rgba(200, 218, 255, 0.5);
  max-width: 400px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.price-value {
  font-size: 0.9rem;
  font-weight: 500;
  color: rgba(200, 218, 255, 0.8);
}

.status-badge,
.status-toggle {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  transition: all 0.2s ease;

  &.status-active {
    background: rgba(76, 175, 80, 0.15);
    border: 1px solid rgba(76, 175, 80, 0.25);

    .v-icon {
      color: #4CAF50;
    }
  }

  &.status-inactive {
    background: rgba(244, 67, 54, 0.1);
    border: 1px solid rgba(244, 67, 54, 0.2);

    .v-icon {
      color: #F44336;
    }
  }
}

.status-toggle {
  cursor: pointer;

  &:hover:not(:disabled) {
    transform: scale(1.1);

    &.status-active {
      background: rgba(76, 175, 80, 0.25);
      border-color: rgba(76, 175, 80, 0.4);
    }

    &.status-inactive {
      background: rgba(244, 67, 54, 0.2);
      border-color: rgba(244, 67, 54, 0.35);
    }
  }

  &:disabled {
    opacity: 0.7;
    cursor: wait;
  }
}

/* Responsive */
@media (max-width: 1024px) {
  .filter-bar {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }

  .search-wrapper {
    max-width: none;
  }

  .filter-group {
    justify-content: space-between;
  }
}

@media (max-width: 768px) {
  .section-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }

  .header-actions {
    width: 100%;
    justify-content: flex-end;
  }

  .table-head,
  .table-row {
    grid-template-columns: 1fr 100px 70px 70px;
  }

  .head-cell,
  .row-cell {
    font-size: 0.85rem;
  }
}

@media (prefers-reduced-motion: reduce) {
  .create-btn,
  .table-row,
  .status-badge,
  .status-toggle,
  .collapse-toggle .v-icon,
  .collapsible-content {
    transition: none;
    animation: none;
  }

  .status-toggle:hover {
    transform: none;
  }
}
</style>
